//@flow
import React from 'react';
import classnames from 'classnames';
import { translate } from 'react-i18next';
import { ButtonWithPopover, ButtonTypes } from 'common/components/core/button';
import { dashboardColors } from 'common/enums/ColorEnum';
import TimeUtils from 'common/utils/TimeUtils';
import moment from 'moment-timezone';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import ToastActions from 'common/redux/actions/toast';
import { themr } from 'react-css-themr';

import { intelliSortSummary } from 'common/middleware/api';
import Statistic from 'common/components/core/stats/Statistic';
import theme from './IntelliSortSummary.scss';
import { Div } from 'common/components/core/wrappers/Div';
import { PopoverPlaceEnum } from 'common/components/core/popover/popOverFactory';
import {
    intersectionObserverFactory,
    IntersectionOverflowButton
} from 'common/components/core/intersection_observer';
import intelliSortIds from 'common/tests/intellisort/intelliSortIds.json';

class OverflowButton extends React.PureComponent {
    render() {
        const {
            invisibleElements,
            visibleElements,
            icon,
            ...others
        } = this.props;
        const areAllElementsInvisible =
            invisibleElements.length &&
            invisibleElements.length === visibleElements.length;
        return (
            <IntersectionOverflowButton
                {...others}
                invisibleElements={invisibleElements}
                icon={!areAllElementsInvisible ? icon : 'bar-chart'}
                ripple={areAllElementsInvisible}
            />
        );
    }
}

const EnhancedIntersectionObserver = intersectionObserverFactory({
    OverflowButton,
    BaseComponent: Div
});

class IntelliSortSummary extends React.PureComponent {
    getDataIntervalId;
    lastUpdatedIntervalID;

    state = {
        totalOrders: null,
        checkedOrdersCount: null,
        acceptedOrdersCount: null,
        rejectedOrdersCount: null,
        accuracy: null,
        lastUpdated: moment()
    };

    getData = () => {
        const {
            clientId,
            homebases,
            selectedDate,
            showErrorToast,
            t
        } = this.props;
        if (!homebases || homebases.size === 0) {
            showErrorToast({
                message: t('fetchSummary.error'),
                timeout: 2000
            });
            return;
        }
        const params = {
            clientId,
            lowerDate: selectedDate,
            higherDate: selectedDate
        };
        if (homebases.size > 0) {
            params.homebases = homebases.map(homebase => homebase.id).join(',');
        }
        intelliSortSummary
            .fetch(params)
            .then(data => {
                this.setState(data);
                this.setState({
                    lastUpdated: moment()
                });
            })
            .catch(() => {
                showErrorToast({
                    message: t('fetchSummary.error'),
                    timeout: 2000
                });
            });
    };

    updatetimeSinceRefresh = () => {
        this.setState({
            timeSinceRefresh: TimeUtils.timeUnitsPassed(
                this.state.lastUpdated,
                's'
            )
        });
    };

    componentDidMount() {
        this.getDataIntervalId = setInterval(this.getData.bind(this), 60000);
        this.lastUpdatedIntervalID = setInterval(
            this.updatetimeSinceRefresh,
            1000
        );
    }

    componentDidUpdate(previousProps) {
        if (previousProps !== this.props) {
            this.getData();
        }
    }

    componentWillUnmount() {
        clearTimeout(this.getDataIntervalId);
        clearTimeout(this.lastUpdatedIntervalID);
    }

    render() {
        const { t } = this.props;
        const timeLabel =
            this.state.timeSinceRefresh > 1
                ? 'fetchSummary.secondsAgo'
                : 'fetchSummary.secondAgo';
        const accuracy =
            this.state.accuracy !== null ? `${this.state.accuracy}%` : null;
        return (
            <div className={classnames(theme.planSummary)}>
                <div className={theme.stats}>
                    <EnhancedIntersectionObserver
                        popoverPlace={PopoverPlaceEnum.Below}
                        popoverClassName={theme.statsIntersectionPopover}
                        invertPopover={false}
                        theme={theme}
                        intersectionWrapClassName={theme.statsIntersectionWrap}
                        className={classnames(theme.statisticsWrap, theme.left)}
                    >
                        <Statistic
                            disablePointerEvents
                            uppercase
                            intersectionId="checked-orders"
                            tooltip={t('fetchSummary.checkedOrders')}
                            tooltipDelay={200}
                            value={this.state.checkedOrdersCount}
                            totalValue={this.state.totalOrders}
                            padLeft
                            title={t('fetchSummary.checkedOrders')}
                            theme={theme}
                            testId={intelliSortIds.checkedOrders}
                        />
                        <Statistic
                            disablePointerEvents
                            uppercase
                            intersectionId="marked-accurate"
                            tooltip={t('fetchSummary.markedAccurate')}
                            tooltipDelay={200}
                            value={this.state.acceptedOrdersCount}
                            totalValue={this.state.totalOrders}
                            padLeft
                            title={t('fetchSummary.markedAccurate')}
                            theme={theme}
                            testId={intelliSortIds.accurateOrders}
                        />
                        <Statistic
                            disablePointerEvents
                            uppercase
                            intersectionId="marked-inaccurate"
                            tooltip={t('fetchSummary.markedInaccurate')}
                            tooltipDelay={200}
                            value={this.state.rejectedOrdersCount}
                            totalValue={this.state.totalOrders}
                            padLeft
                            title={t('fetchSummary.markedInaccurate')}
                            theme={theme}
                            testId={intelliSortIds.inaccurateOrders}
                        />
                        <Statistic
                            disablePointerEvents
                            uppercase
                            intersectionId="accuracy"
                            tooltip={t('fetchSummary.accuracy')}
                            tooltipDelay={200}
                            value={accuracy}
                            padLeft
                            title={t('fetchSummary.accuracy')}
                            theme={theme}
                            testId={intelliSortIds.accuracy}
                        />
                    </EnhancedIntersectionObserver>
                    <div className={theme.right}>
                        <div>
                            {t('fetchSummary.lastUpdated')}:
                            {this.state.timeSinceRefresh} {t(timeLabel)}
                        </div>
                        <ButtonWithPopover
                            className={theme.btn}
                            theme={theme}
                            buttonType={ButtonTypes.Text}
                            color={dashboardColors.darkSkyBlue.name}
                            onClick={this.getData}
                            label={t(`reloadLabel`)}
                            testId={intelliSortIds.summaryReload}
                            semibold
                            uppercase
                        />
                    </div>
                </div>
            </div>
        );
    }
}

const withRedux = connect(null, {
    showErrorToast: ToastActions.showErrorToast
});

const ThemedIntelliSortSummary = compose(
    withRedux,
    themr(theme),
    translate('intelliSort')
)(IntelliSortSummary);
export { ThemedIntelliSortSummary as IntelliSortSummary };
